import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { SessionStore, UserInfo } from './session.store';
import { environment } from '@env/environment';
import { tap, catchError, map, take, mergeMap, shareReplay, switchMap } from 'rxjs/operators';
import { throwError, Observable, of, from } from 'rxjs';
import { applyTransaction, resetStores } from '@datorama/akita';
import {
	LoginStatus,
	PasswordRegistrationResponse,
	PasswordRegistrationRequest,
	DefaultStatus,
	InitialSignUpRequest,
	InitialSignUpResponse
} from '@app-model/login';
import { AmplifyService } from '@app-services/amplify.service';
import { CreateAccountReferralErrorStatus } from '@app/views/private/refer-and-earn/enums/referralErrorStatus.enum';

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	constructor(private sessionStore: SessionStore, private http: HttpClient, private amplifyService: AmplifyService) {}

	setLoading(status: boolean) {
		this.sessionStore.setLoading(status);
	}

	checkEmailStatus(email: string): Observable<LoginStatus> {
		this.setLoading(true);
		return this.http.get(`${environment.api}/login/status/${email}`).pipe(
			map((response: LoginStatus) => response),
			tap(() => this.sessionStore.setLoading(false)),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	createAccount(req: InitialSignUpRequest): Observable<InitialSignUpResponse> {
		return this.http.post<InitialSignUpResponse>(`${environment.api}/login/createAccount`, req).pipe(
			catchError(err => {
				const referralStatus: CreateAccountReferralErrorStatus =
					err?.error?.errors?.length && err.error.errors[0] && err.error.errors[0].status.includes('REFERRAL_CODE_')
						? (err.error.errors[0].status as CreateAccountReferralErrorStatus)
						: null;
				return throwError({ ...(err || {}), referralCodeErrorStatus: referralStatus });
			})
		);
	}

	resetPassword(userId: string, verifCode: string, password: string): Observable<PasswordRegistrationResponse> {
		this.setLoading(true);
		const request: PasswordRegistrationRequest = {
			userId,
			verifCode,
			password
		};
		return this.http.post(`${environment.api}/login/resetPassword`, request).pipe(
			map((response: PasswordRegistrationResponse) => response),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	verifyUser(userId: string, verifCode: string): Observable<DefaultStatus> {
		return this.http
			.get(`${environment.api}/login/verifyUser/${userId}/${verifCode}`)
			.pipe(map((response: DefaultStatus) => response));
	}

	forgotPassword(email: string) {
		this.setLoading(true);
		return this.http.get(`${environment.api}/login/resetPassword/${email}`).pipe(
			take(1),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	isAuthenticated(): Observable<boolean> {
		return from(this.amplifyService.isAuthenticated()).pipe(
			tap(isAuthenticated => {
				this.sessionStore.update({ isAuthenticated });
			})
		);
	}

	updateIsAuthenticated(isAuthenticated: boolean) {
		applyTransaction(() => {
			this.sessionStore.setLoading(false);
			this.sessionStore.update({ isAuthenticated });
		});
	}

	logout(): Observable<void> {
		return from(this.amplifyService.logout()).pipe(
			tap(() => {
				this.sessionStore.clearSession();
				this.sessionStore.reset();
				resetStores();
			})
		);
	}

	registerUser(userId: string, verifCode: string, values): Observable<PasswordRegistrationResponse> {
		return this.http.post(`${environment.api}/login/registerNewUser/${userId}/${verifCode}`, values).pipe(
			take(1),
			map((response: PasswordRegistrationResponse) => response),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	// TODO -> refactor this to a subject. remove akita.
	getUser(): Observable<UserInfo> {
		return this.sessionStore
			._select(state => state.userInfo)
			.pipe(
				mergeMap(userInfo => {
					if (userInfo) {
						return of(userInfo);
					}
					return this.getUserFromBack();
				})
			);
	}

	getUserFromBack(): Observable<UserInfo> {
		return this.http.get(`${environment.api}/user/info`).pipe(
			map((userInfo: UserInfo) => Object.assign(new UserInfo(), userInfo)),
			tap(userInfo => this.sessionStore.update({ userInfo })),
			shareReplay()
		);
	}

	login(username: string, password: string): Observable<UserInfo> {
		return from(this.amplifyService.login(username, password)).pipe(
			tap(() => this.updateIsAuthenticated(true)),
			switchMap(() => this.getUser().pipe(take(1))),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	hasPendingRegistration(): Observable<boolean> {
		return this.getUser().pipe(
			take(1),
			map(user => user?.pendingRegistration)
		);
	}
}
